import { Component, OnInit } from "@angular/core";
import { faSave, faTrash } from "@fortawesome/free-solid-svg-icons";
import { NotifierService } from "angular-notifier";
import { FormField } from "src/app/interfaces/form-field.model";
import { FobInvoicePriceService } from "src/app/services/autopac/fob-invoice-price.service";

interface FobInvoiceEntry {
  Model: string;
  Colour: string;
  Unit: number;
  ExchangeRate: number;
  FobUS: number;
  Freight: number;
}

@Component({
  selector: "app-fob-invoice-price",
  templateUrl: "./fob-invoice-price.component.html",
  styleUrls: ["./fob-invoice-price.component.css"],
})
export class FobInvoicePriceComponent implements OnInit {
  constructor(
    private fobInvoicePriceService: FobInvoicePriceService,
    private notifier: NotifierService,
  ) {}

  faSave = faSave;
  faTrash = faTrash;
  loading = false;
  saving = false;
  loadedInvoiceNo = "";
  private selectedEntry: Record<string, any> | null = null;

  fobTable: FormField[] = [
    {
      type: "select",
      id: "invoiceNo",
      label: "Invoice No",
      required: true,
      options: [],
    },
    { type: "text", id: "model", label: "Model", lock: true, required: true },
    { type: "text", id: "colour", label: "Colour", lock: true, required: true },
    { type: "number", id: "unit", label: "Unit", lock: true, required: true },
    {
      type: "number",
      id: "exchangeRate",
      label: "Exchange Rate (Bank Rate)",
      required: true,
    },
    { type: "number", id: "fob", label: "FOB US$", required: true },
    { type: "number", id: "freight", label: "Freight", required: true },
  ];

  fob_headers = [
    { title: "MODEL", type: "data", id: "model", classes: "max-w-64" },
    { title: "COLOUR", type: "data", id: "colour", classes: "max-w-64" },
    { title: "UNIT", type: "data", id: "unit", classes: "max-w-64" },
    {
      title: "EXCHANGE RATE",
      type: "data",
      id: "exchangeRate",
      classes: "max-w-64",
    },
    { title: "FOB", type: "data", id: "fob", classes: "max-w-64" },
    { title: "FREIGHT", type: "data", id: "freight", classes: "max-w-64" },
    { title: "ACTION", type: "edit" },
  ];

  fob_entries: Array<Record<string, any>> = [];

  ngOnInit(): void {
    this.fobInvoicePriceService.getInvoiceNoList().subscribe({
      next: (response) => {
        if (response.statusCode !== 200 || !Array.isArray(response.body)) {
          this.notifier.notify(
            "error",
            response.message || "Unable to retrieve invoice numbers.",
          );
          return;
        }

        const invoiceField = this.fobTable.find(
          (field) => field.id === "invoiceNo",
        );
        if (invoiceField) {
          invoiceField.options = response.body
            .filter((invoiceNo: unknown): invoiceNo is string =>
              typeof invoiceNo === "string",
            )
            .map((invoiceNo: string) => ({
              display: invoiceNo,
              value: invoiceNo,
            }));
        }
      },
      error: () =>
        this.notifier.notify("error", "Unable to retrieve invoice numbers."),
    });
  }

  handleUpdate(event: { fields: FormField[] }): void {
    const invoiceNo = String(
      event.fields.find((field) => field.id === "invoiceNo")?.value ?? "",
    );
    if (invoiceNo && invoiceNo === this.loadedInvoiceNo) {
      this.syncSelectedEntry(event.fields);
      return;
    }
    if (!invoiceNo) {
      this.loadedInvoiceNo = "";
      this.selectedEntry = null;
      this.fob_entries = [];
      this.clearDetails();
      return;
    }

    this.loadedInvoiceNo = invoiceNo;
    this.selectedEntry = null;
    this.loading = true;
    this.fob_entries = [];
    this.clearDetails();

    this.fobInvoicePriceService.getPurchaseInvoiceList(invoiceNo).subscribe({
      next: (response) => {
        this.loading = false;
        if (response.statusCode !== 200 || !Array.isArray(response.body)) {
          this.notifier.notify(
            "error",
            response.message || "Unable to retrieve invoice details.",
          );
          return;
        }
        this.fob_entries = response.body.map((entry: FobInvoiceEntry) => ({
          model: entry.Model,
          colour: entry.Colour,
          unit: entry.Unit,
          exchangeRate: entry.ExchangeRate,
          fob: entry.FobUS,
          freight: entry.Freight,
        }));
      },
      error: () => {
        this.loading = false;
        this.loadedInvoiceNo = "";
        this.notifier.notify("error", "Unable to retrieve invoice details.");
      },
    });
  }

  editEntry(entry: Record<string, any>): void {
    this.selectedEntry = entry;
    const invoiceNo = this.fobTable.find(
      (field) => field.id === "invoiceNo",
    )?.value;
    this.fobTable.forEach((field) => {
      if (field.id === "invoiceNo") {
        field.value = invoiceNo;
      } else if (Object.prototype.hasOwnProperty.call(entry, field.id)) {
        field.value = entry[field.id];
      }
    });
  }

  private syncSelectedEntry(fields: FormField[]): void {
    if (!this.selectedEntry) return;

    const fieldToEntryKey: Record<string, string> = {
      model: "model",
      colour: "colour",
      unit: "unit",
      exchangeRate: "exchangeRate",
      fob: "fob",
      freight: "freight",
    };
    fields.forEach((field) => {
      const key = fieldToEntryKey[field.id];
      if (key) this.selectedEntry![key] = field.value;
    });
    this.fob_entries = [...this.fob_entries];
  }

  updateFOB(): void {
    if (this.saving || this.loading) return;

    const value = (id: string) =>
      this.fobTable.find((field) => field.id === id)?.value;
    const invoiceNo = String(value("invoiceNo") ?? "").trim();
    const model = String(value("model") ?? "").trim();
    const colour = String(value("colour") ?? "").trim();
    const unit = Number(value("unit"));
    const exchangeRate = Number(value("exchangeRate"));
    const fobUS = Number(value("fob"));
    const freight = Number(value("freight"));
    const editableValues = [value("exchangeRate"), value("fob"), value("freight")];

    if (
      !invoiceNo ||
      !model ||
      !colour ||
      !Number.isInteger(unit) ||
      unit < 1 ||
      editableValues.some((item) => item === "" || item == null) ||
      !Number.isFinite(exchangeRate) ||
      exchangeRate <= 0 ||
      !Number.isFinite(fobUS) ||
      fobUS < 0 ||
      !Number.isFinite(freight) ||
      freight < 0
    ) {
      this.notifier.notify(
        "error",
        "Select an invoice and table row, then enter a valid exchange rate, FOB price and freight.",
      );
      return;
    }

    const payload = {
      PurchaseInvoiceNo: invoiceNo,
      Model: model,
      Colour: colour,
      Unit: unit,
      ExchangeRate: exchangeRate,
      FobUS: fobUS,
      FobSG: unit * exchangeRate * fobUS,
      Freight: freight,
    };

    this.saving = true;
    this.fobInvoicePriceService.updateVehiclePrice(payload).subscribe({
      next: (response) => {
        this.saving = false;
        if (response.code !== 0) {
          this.notifier.notify(
            "error",
            response.message || "Unable to update FOB invoice price.",
          );
          return;
        }

        if (this.selectedEntry) {
          this.selectedEntry["exchangeRate"] = exchangeRate;
          this.selectedEntry["fob"] = fobUS;
          this.selectedEntry["freight"] = freight;
          this.fob_entries = [...this.fob_entries];
        }
        this.clearDetails();
        this.notifier.notify("success", "FOB Invoice Price updated successfully.");
      },
      error: () => {
        this.saving = false;
        this.notifier.notify("error", "Unable to update FOB invoice price.");
      },
    });
  }

  clearFOB(): void {
    if (this.saving) return;
    this.clearDetails();
  }

  private clearDetails(): void {
    this.fobTable.forEach((field) => {
      if (field.id !== "invoiceNo") field.value = "";
    });
  }

}
